import { push } from 'redux-first-history';
import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import moment from 'moment';
import { keyByProperty } from '../../../helpers/core';
import actions from './actions';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import Api from '../../../helpers/api';
import { recurrenceType } from '../helpers/helpers';
import { t } from '../../../i18n';

const managersApi = new Api('administrators/list?pageSize=9999');
const recentLocationsApi = new Api('orientations/recent_locations');
const createOrientationsApi = new Api('orientations');

function* loadManagersRequest() {
  try {
    const result = yield call(sendRequest, managersApi.load, {}, notifications.REQUEST_ERROR);

    if (result) {
      const { data } = result;
      const managers = [];

      if (data && data.data && data.data.length > 0) {
        data.data.forEach(manager => {
          const { id, disabled, first_name, last_name, role } = manager;
          const name = `${first_name} ${last_name}`;
          managers.push({
            id,
            disabled,
            name: role && role.name ? `${name} - ${role.name}` : name,
          });
        });
      }

      yield put(actions.loadManagersSuccess({ data: managers }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadRecentLocationsRequest() {
  try {
    const result = yield call(sendRequest, recentLocationsApi.load, {}, notifications.REQUEST_ERROR);

    if (result && result.data && result.data.success && result.data.success.data) {
      yield put(
        actions.loadRecentLocationsSuccess({
          data: result.data.success.data.records,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* createOrientationRequest() {
  try {
    const getSessionAddState = state => state.SessionAdd;
    const sessionAddState = yield select(getSessionAddState);
    const { nameForm, addressForm, primaryConstactForm, bookingForm, frequency, isRecurringSelected } = sessionAddState;

    // Name
    const nameFormPropsArray = Object.values(nameForm);
    const nameData = keyByProperty(nameFormPropsArray, 'name', prop => prop.value);

    // Address
    const addressFormPropsArray = Object.values(addressForm);
    const addressData = keyByProperty(addressFormPropsArray, 'name', prop => prop.value);

    // Primary Constact
    const primaryConstactFormArray = Object.values(primaryConstactForm);
    const primaryConstactData = keyByProperty(primaryConstactFormArray, 'name', prop => prop.value);

    // Booking
    const bookingFormArray = Object.values(bookingForm);
    const bookingFormData = keyByProperty(bookingFormArray, 'name', prop => prop.value);

    const bookingDays = bookingFormData.days;
    const startDate = moment(bookingDays[0].date, 'DD/MM/YYYY').format('YYYY-MM-DD');
    const endDate = moment(bookingDays[bookingDays.length - 1].date, 'DD/MM/YYYY').format('YYYY-MM-DD');
    const start_date = moment(startDate).format('YYYY-MM-DD');
    const end_date = moment(endDate).format('YYYY-MM-DD');

    const jobs = [];
    bookingFormData.days.forEach(day => {
      const bookings = [];

      day.bookings.forEach(booking => {
        const bookingArray = Object.values(booking);
        const bookingData = keyByProperty(bookingArray, 'name', prop => prop.value);

        const formattedDate = moment(day.date, 'DD/MM/YYYY').format('YYYY-MM-DD');
        const starts_at = moment(`${formattedDate} ${bookingData.starts_at}`).format();
        const finishes_at = moment(`${formattedDate} ${bookingData.finishes_at}`).format();

        bookings.push({
          starts_at,
          finishes_at,
          workers_required: bookingData.workers_required,
          attire_items: bookingData.attire_items,
          special_instructions: bookingData.special_instructions,
        });
      });

      jobs.push({ bookings });
    });

    const result = yield call(
      sendRequest,
      createOrientationsApi.multipleParamsCreateItem,
      {
        data: {
          orientation: {
            ...nameData,
            ...addressData.address,
            recurrence: isRecurringSelected ? frequency : recurrenceType.once_off, // once_off, daily, weekly, monthly,
            orientation_primary_contact_id: primaryConstactData.contact,
            start_date,
            end_date,
            jobs,
          },
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      const { data } = result;

      yield put(actions.createOrientationSuccess({ data }));
      yield put(push(`/sessions/${data.orientation.id}/settings`));

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.created_successfully', { item: t('dash.orientations.session_title') }),
        }),
      );
    } else {
      yield put(actions.createOrientationFailed());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: (result.job && result.job[0]) || (result.booking && result.booking[0]),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actions.LOAD_MANAGERS_REQUEST, loadManagersRequest)]);
  yield all([takeEvery(actions.LOAD_RECENT_LOCATIONS_REQUEST, loadRecentLocationsRequest)]);
  yield all([takeEvery(actions.CREATE_ORIENTATION_REQUEST, createOrientationRequest)]);
}
